<template>
    <div>
        <Component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <Component :is="story.content.component" v-if="story" :blok="story.content" :products="products" />
        </main>
        <Component
            :is="globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '~/../_base/store/global';
import type { IAirportProducts } from '../../../../packages/sb-components/lib/types';
import { getProductsFromResponse } from '~/services/apiProductService';

// Constant variable for access to quote store
const globalStore: any = useGlobalStore();

const { locale } = useI18n();

// Constant variable for access to route information
const route: any = useRoute();

// Constant variable for access to Runtime Config
const config = useRuntimeConfig();

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = ['page.globals', 'PSFAirportPage.globals', 'PSFAirportPage.AirportEntity'];

const sbLocation = `/${config.public.STORYBLOK_FOLDER}/pages/airports/parking/${route.params.IATA}`;

// Perform request to Storyblok to retrieve content
const story: any = await useAsyncStoryblok(sbLocation, {
    version: config.public.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
    language: locale.value,
    resolve_relations: resolveRelations,
});

// Send user to error page if no story found
if (!story.value) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
} else {
    setSeoMeta(story.value.content.seo);
    globalStore.update(story.value.content.globals);
}

const airport: any = story.value.content.AirportEntity.content;
const apiEntity: any = (story.value.content.api_entity || [])[0];
const slug = apiEntity?.og_slug ? apiEntity?.og_slug : 'airport-parking';
const slugUrl = apiEntity?.og_slug_url
    ? `https://parksleepfly.com/${apiEntity?.og_slug_url}`
    : `https://parksleepfly.com/${airport.code?.toLowerCase()}/airport-parking`;

const psf: any = await useAsyncData(
    `${airport.code?.toLowerCase()}/airport-parking`,
    async () =>
        await $privateApi('contents/pages', {
            method: 'POST',
            body: {
                domain: 'psf.com',
                subdomain: airport.code?.toLowerCase(),
                slug: slug,
                full_url: slugUrl,
            },
        })
);

const products: IAirportProducts = getProductsFromResponse(psf);
if (!process.server) {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), { resolveRelations });
}
</script>
